import { OverlayLifts } from "../flatbuffers/generated/archive/overlay-lifts";
import { formatUuidT } from "../flatbuffers/display";

/**
 * Bank overlays, as the two bank editors author them.
 *
 * A bank may name another bank it "draws on" (`Archive.Bank.overlay_bank_id`):
 * VTS then re-publishes this bank's control state on the target bank's cars,
 * so a module bank nobody watches — a Numato discrete-input bank reporting
 * which floors are secured, say — shows its padlocks on the elevator bank
 * operators do watch. `overlay_lifts` says which of the target's cars it draws
 * on, and `overlay_provided` marks a save as authoritative over both.
 *
 * LiftCloud enforces two rules, and the editors apply them first so the
 * operator is never offered a choice the server will refuse:
 *
 *   - the target is another bank of the same organization on the same edge
 *     server (a server belongs to one organization, so "same server" covers
 *     both);
 *   - a bank is a source or a target, never both.
 *
 * The Setup Wizard's Banks step and the standalone bank form share this module
 * so the two cannot drift apart on either rule, on the wire shape of a save, or
 * on the copy the operator reads.
 */

/** Caption of the target picker. */
export const OVERLAY_TARGET_LABEL = "Draw on bank";

/** Caption of the lifts-mode picker. */
export const OVERLAY_LIFTS_LABEL = "Which lifts";

/** What the pair of controls does, shown beside them. */
export const OVERLAY_HELP =
  "Draws this bank's control state — such as a secured floor's padlock — on the chosen bank's cars. Usually set on a hidden module bank (a relay or discrete-input module).";

/** Why a bank that something draws on is offered no target. */
export function overlayDrawnOnByMessage(sourceNames: readonly string[]): string {
  return `This bank is drawn on by ${sourceNames.join(", ")}; a bank cannot both draw and be drawn on.`;
}

/** One bank as the overlay picker needs to know it. */
export interface OverlayBankRef {
  id: string;
  name: string;
  /** The bank's channel. A bank's server is a property of its channel. */
  channelId: string;
  /** The bank this one draws on; "" when it draws on none. */
  overlayBankId: string;
}

/** The targets a bank may choose, or why it may choose none. */
export interface OverlayTargetChoice {
  targets: { id: string; name: string }[];
  /**
   * Names of the banks drawing on this one. Non-empty means this bank is a
   * target, so it may not also be a source, and `targets` is empty.
   */
  drawnOnBy: string[];
}

function sameId(a: string, b: string): boolean {
  return a !== "" && a.toLowerCase() === b.toLowerCase();
}

/**
 * Which banks `bankId` may draw on.
 *
 * `bankId` is null for a bank not created yet: nothing can draw on it, so only
 * the target-side rules apply. `serverChannelIds` is the set of channels of the
 * edge server the bank sits on, and a candidate whose channel is not in it is on
 * another server; pass null when `banks` was already fetched for that server and
 * carries no channel to check.
 */
export function overlayTargetChoice(
  bankId: string | null,
  banks: readonly OverlayBankRef[],
  serverChannelIds: ReadonlySet<string> | null,
): OverlayTargetChoice {
  const self = bankId ?? "";
  const others = banks.filter((b) => !sameId(self, b.id));
  const drawnOnBy = others.filter((b) => sameId(self, b.overlayBankId)).map((b) => b.name);
  if (drawnOnBy.length > 0) return { targets: [], drawnOnBy };

  const targets = others
    .filter((b) => b.overlayBankId === "")
    .filter((b) => serverChannelIds === null || serverChannelIds.has(b.channelId))
    .map((b) => ({ id: b.id, name: b.name }));
  return { targets, drawnOnBy: [] };
}

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

/**
 * `value` as an `OverlayLifts` member, or the schema default for anything the
 * enum does not define. Checked through the enum's own reverse mapping, so a
 * member appended to the schema is read without touching this function.
 */
function toOverlayLifts(value: unknown): OverlayLifts {
  if (typeof value === "number" && typeof OverlayLifts[value] === "string") {
    return value as OverlayLifts;
  }
  return OverlayLifts.ALL_LIFTS;
}

/**
 * The overlay a loaded bank carries, from either spelling: a bank decoded from
 * a FlatBuffer response holds `overlayBankId` (a `UuidT`) and `overlayLifts`; a
 * JSON one holds `overlay_bank_id` (a string) and `overlay_lifts`. No overlay
 * reads as `""`, never as a zero UUID that a picker would show as a bank.
 */
export function readBankOverlay(bank: unknown): {
  overlayBankId: string;
  overlayLifts: OverlayLifts;
} {
  const record = (bank ?? {}) as Record<string, unknown>;
  const rawId = (record.overlayBankId ?? record.overlay_bank_id ?? null) as Parameters<
    typeof formatUuidT
  >[0];
  const id = formatUuidT(rawId);
  return {
    overlayBankId: id === ZERO_UUID ? "" : id,
    overlayLifts: toOverlayLifts(record.overlayLifts ?? record.overlay_lifts),
  };
}

/**
 * The request fields of a save that is authoritative over the overlay.
 *
 * Both editors render the overlay controls for every bank, so every save they
 * make carries `overlay_provided: true` — exactly as an OPC bank's save carries
 * `vendor_paths_provided`. With a target the save writes it and the lifts mode;
 * without one it sends the flag alone, which is how LiftCloud is told to clear
 * a stored overlay (a save without the flag leaves it untouched).
 */
export function overlaySaveFields(
  targetId: string | null,
  lifts: OverlayLifts,
): Record<string, unknown> {
  if (!targetId) return { overlay_provided: true };
  return { overlay_bank_id: targetId, overlay_lifts: lifts, overlay_provided: true };
}

/**
 * Every key a bank record or request body can carry an overlay under: the
 * request fields in both spellings, and the response-only `overlay_sources`.
 */
const OVERLAY_KEYS = [
  "overlay_bank_id",
  "overlayBankId",
  "overlay_lifts",
  "overlayLifts",
  "overlay_provided",
  "overlayProvided",
  "overlay_sources",
] as const;

/**
 * `body` as an authoritative overlay save: whatever overlay fields it already
 * carries, under either spelling, are dropped and the editor's own replace
 * them. Returns a new object; `body` is not modified.
 *
 * The dropping is the point. A body spread from a loaded bank still holds the
 * overlay the bank was loaded with, and the serializer reads either spelling;
 * merely adding the new fields on top would let a stale `overlay_bank_id`
 * survive a clear — the flag alone means "clear" only when no id rides along.
 */
export function withOverlaySave(
  body: Record<string, unknown>,
  targetId: string | null,
  lifts: OverlayLifts,
): Record<string, unknown> {
  const kept = Object.fromEntries(
    Object.entries(body).filter(([key]) => !(OVERLAY_KEYS as readonly string[]).includes(key)),
  );
  return { ...kept, ...overlaySaveFields(targetId, lifts) };
}
